import { API_CONFIG } from '../../../config/api.js';

const ALL_COMBAT_CARD_IDS = [
  'basic_attack',
  'full_attack',
  'cast_simple_spell',
  'complex_spell',
  'defend',
  'dodge',
  'help',
  'heal',
  'hide',
  'search',
  'grapple',
  'shove',
  'intimidate',
  'ready_action',
  'rest',
  'recover',
  'retreat',
  'end_turn',
];

const CATEGORY_THEMES = {
  attack: { border: '#9f4337', accent: '#e3b272', glow: '#70251f', sigil: '✦' },
  spell: { border: '#4a5f9d', accent: '#d7c7f4', glow: '#29365d', sigil: '✶' },
  defense: { border: '#7d6740', accent: '#e6d29e', glow: '#4d3f26', sigil: '⛨' },
  movement: { border: '#8b7449', accent: '#ead7a6', glow: '#4d3f2b', sigil: '➶' },
  support: { border: '#4f7a69', accent: '#d3eadc', glow: '#2e4a40', sigil: '✢' },
  utility: { border: '#6b5848', accent: '#e6d6bb', glow: '#3d3026', sigil: '◌' },
};

const FACE_PRESENTATION = {
  basic_attack: { scale: 1, translateY: '0%' },
  full_attack: { scale: 1, translateY: '0%' },
  cast_simple_spell: { scale: 1, translateY: '0%' },
  complex_spell: { scale: 1, translateY: '0%' },
  defend: { scale: 1, translateY: '0%' },
  search: { scale: 1, translateY: '0%' },
  help: { scale: 1, translateY: '0%' },
  heal: { scale: 1, translateY: '0%' },
  hide: { scale: 1, translateY: '0%' },
  dodge: { scale: 1, translateY: '0%' },
  grapple: { scale: 1, translateY: '0%' },
  shove: { scale: 1, translateY: '0%' },
  intimidate: { scale: 1, translateY: '0%' },
};

function titleize(cardId) {
  return cardId.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function chunkTitle(name) {
  const words = name.split(' ');
  if (words.length <= 2) return [name];
  const midpoint = Math.ceil(words.length / 2);
  return [words.slice(0, midpoint).join(' '), words.slice(midpoint).join(' ')];
}

function escapeSvg(value) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

export function buildPlaceholderFaceSvg(cardId, category) {
  const theme = CATEGORY_THEMES[category] || CATEGORY_THEMES.utility;
  const lines = chunkTitle(titleize(cardId));
  const titleMarkup = lines
    .map(
      (line, index) => `
      <text
        x="360"
        y="${468 + (index * 46)}"
        fill="#f5ead5"
        text-anchor="middle"
        font-family="Cinzel, Georgia, serif"
        font-size="${lines.length > 1 ? 38 : 44}"
        letter-spacing="2"
      >${escapeSvg(line.toUpperCase())}</text>`,
    )
    .join('');

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 720 1120" role="img" aria-label="${escapeSvg(titleize(cardId))}">
      <defs>
        <linearGradient id="outer" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stop-color="${theme.border}" />
          <stop offset="100%" stop-color="${theme.glow}" />
        </linearGradient>
        <linearGradient id="banner" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stop-color="#f5e8c8" />
          <stop offset="100%" stop-color="#d3b27a" />
        </linearGradient>
        <radialGradient id="field" cx="50%" cy="20%" r="85%">
          <stop offset="0%" stop-color="#3a322f" />
          <stop offset="100%" stop-color="#161211" />
        </radialGradient>
      </defs>
      <rect width="720" height="1120" rx="36" fill="url(#outer)" />
      <rect x="22" y="22" width="676" height="1076" rx="30" fill="#eadfc8" />
      <rect x="40" y="40" width="640" height="1040" rx="26" fill="#1c1715" stroke="#6a5642" stroke-width="6" />
      <rect x="64" y="64" width="592" height="992" rx="22" fill="url(#field)" stroke="${theme.accent}" stroke-opacity="0.42" stroke-width="4" />
      <rect x="112" y="94" width="496" height="104" rx="24" fill="url(#banner)" stroke="#7d623f" stroke-width="4" />
      <rect x="112" y="922" width="496" height="104" rx="24" fill="url(#banner)" stroke="#7d623f" stroke-width="4" />
      <path d="M118 126h-30l22-26h38" fill="none" stroke="#7d623f" stroke-width="5" />
      <path d="M602 126h30l-22-26h-38" fill="none" stroke="#7d623f" stroke-width="5" />
      <path d="M118 994h-30l22 26h38" fill="none" stroke="#7d623f" stroke-width="5" />
      <path d="M602 994h30l-22 26h-38" fill="none" stroke="#7d623f" stroke-width="5" />
      <rect x="118" y="224" width="484" height="646" rx="30" fill="none" stroke="${theme.accent}" stroke-opacity="0.25" stroke-width="4" />
      <circle cx="360" cy="370" r="128" fill="none" stroke="${theme.accent}" stroke-opacity="0.22" stroke-width="5" />
      <circle cx="360" cy="370" r="176" fill="none" stroke="${theme.accent}" stroke-opacity="0.12" stroke-width="2.5" stroke-dasharray="10 14" />
      <text x="360" y="395" fill="${theme.accent}" text-anchor="middle" font-family="Cinzel, Georgia, serif" font-size="124">${theme.sigil}</text>
      <text x="360" y="620" fill="${theme.accent}" fill-opacity="0.72" text-anchor="middle" font-family="Cinzel, Georgia, serif" font-size="26" letter-spacing="10">FABLE TABLE</text>
      ${titleMarkup}
      <text x="360" y="164" fill="#201915" text-anchor="middle" font-family="Cinzel, Georgia, serif" font-size="52">${escapeSvg(titleize(cardId))}</text>
      <text x="360" y="992" fill="#201915" text-anchor="middle" font-family="Cinzel, Georgia, serif" font-size="52">${escapeSvg(titleize(cardId))}</text>
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

// Mirrors DEFAULT_CARD_DECK in backend/src/gaia/services/image_lab/deck_access.py.
// Used when the user's PlayerPreferences haven't loaded yet.
export const DEFAULT_CARD_DECK = 'monochrome_woodcut_2026-04-27';

// Sentinel deck name (matches ORIGINAL_DECK in backend deck_access.py). Selecting
// this means "render the canonical /faces/ images directly, skip the deck path."
export const ORIGINAL_DECK = 'default';

// BOU-764: cardUrlMap (when provided) overrides the legacy
// /assets/combat-cards/decks/<deck>/<id>.webp path. The map comes from
// /api/preferences/player/available-decks via PlayerPreferencesContext
// and points at the backend deck proxy (/api/combat-card-decks/...) for
// named decks, or /faces/<id>.webp for the synthetic "default" deck.

// BOU-1111: once-per-session set of (deck) we've already warned about so a
// missing named deck logs exactly one console.warn, not one per card render.
const _warnedFallbackDecks = new Set();

function resolveDeckCardUrl(url) {
  if (typeof url !== 'string' || !url) {
    return url;
  }
  if (url.startsWith('/api/combat-card-decks/') && API_CONFIG.BACKEND_URL) {
    return `${API_CONFIG.BACKEND_URL}${url}`;
  }
  return url;
}

function buildDeckArtEntry(cardId, deckName, cardUrlMap) {
  const facesPath = `/assets/combat-cards/faces/${cardId}.webp`;
  const deck = deckName || DEFAULT_CARD_DECK;
  const isOriginal = deck === ORIGINAL_DECK;

  let preferredFaceSrc;
  const hasLoadedCardUrlMap = cardUrlMap && typeof cardUrlMap === 'object';

  if (hasLoadedCardUrlMap && cardUrlMap[cardId]) {
    preferredFaceSrc = resolveDeckCardUrl(cardUrlMap[cardId]);
  } else if (isOriginal) {
    preferredFaceSrc = facesPath;
  } else {
    // Backward-compat: no URL map yet (e.g. /available-decks request in
    // flight or failed), or the loaded map lacks this card. Fall back to the
    // canonical /faces/ deck rather than the legacy /decks/ static path,
    // since the decks dir is being removed from git as part of BOU-764.
    preferredFaceSrc = facesPath;
    if (hasLoadedCardUrlMap && !_warnedFallbackDecks.has(deck)) {
      _warnedFallbackDecks.add(deck);
      console.warn(
        '[combatCardArt] Named deck has no URL for card; falling back to /faces/. ' +
        'This usually means the deck is missing from the backend card_deck_store.',
        { deck, cardId },
      );
    }
  }

  return {
    preferredFaceSrc,
    fallbackFaceSrc: facesPath,
    faceAlt: `${titleize(cardId)} tarot combat card illustration`,
    presentation: FACE_PRESENTATION[cardId] || { scale: 1, translateY: '0%' },
  };
}

export const COMBAT_CARD_FACE_ART = Object.fromEntries(
  ALL_COMBAT_CARD_IDS.map((cardId) => [cardId, buildDeckArtEntry(cardId)]),
);

export function getCombatCardArt(cardId, category = 'utility', deckName, cardUrlMap) {
  if (!cardId) return null;

  const art = ALL_COMBAT_CARD_IDS.includes(cardId)
    ? buildDeckArtEntry(cardId, deckName, cardUrlMap)
    : {
        preferredFaceSrc: cardUrlMap?.[cardId] || null,
        fallbackFaceSrc: null,
        faceAlt: `${titleize(cardId)} tarot combat card illustration`,
        presentation: { scale: 1, translateY: '0%' },
      };

  return {
    ...art,
    placeholderFaceSrc: buildPlaceholderFaceSvg(cardId, category),
  };
}
